import { getSubmissions, setSubmissions } from './cookies';

interface LeadEventParams {
  formSubmitId: number;
  formSubmitPage: string;
  formSubmitTitle: string;
  formSubmitNumOfFields: number;
  formSubmitLocation: string;
  formSubmitSiteId: string;
}

type AnalyticsWindow = typeof window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

const analytics = window as AnalyticsWindow;

/**
 * Fire the `oc_lead_form` conversion event through both analytics paths.
 *
 * Member sites are wired one of two ways (see AsyncTrackingCode.php):
 *
 * - GTM container — tags react to `dataLayer.push({ event: 'oc_lead_form' })`.
 * - Direct GA4 (gtag.js, no GTM) — gtag.js ignores raw dataLayer event objects,
 *   so these sites need an explicit `gtag('event', 'oc_lead_form', ...)` call.
 *
 * Firing both keeps parity with the original `forms.js` behavior so neither
 * member segment loses the event. The GA4 tag may be deferred until window
 * `load`, so the gtag call is queued until `gtag` is defined.
 */
const fireLeadEvent = (params: LeadEventParams) => {
  analytics.dataLayer = analytics.dataLayer || [];
  analytics.dataLayer.push({ event: 'oc_lead_form', ...params });

  // The original gtag payload intentionally omits the site id.
  const gtagParams = {
    formSubmitId: params.formSubmitId,
    formSubmitPage: params.formSubmitPage,
    formSubmitTitle: params.formSubmitTitle,
    formSubmitNumOfFields: params.formSubmitNumOfFields,
    formSubmitLocation: params.formSubmitLocation,
  };

  const sendGtagEvent = () => {
    if (typeof analytics.gtag === 'function') {
      analytics.gtag('event', 'oc_lead_form', gtagParams);
      return true;
    }
    return false;
  };

  if (!sendGtagEvent()) {
    window.addEventListener('load', sendGtagEvent, { once: true });
  }
};

/**
 * Replay pending Gravity Forms lead-conversion events.
 *
 * The `oncarrot_forms` cookie is written server-side after Gravity Forms
 * accepts a submission (oc-functionality-gforms-events.php) with
 * `eventposted: 0`. The matching `oc_lead_form` event has to fire on whatever
 * page the visitor lands on next. For redirect confirmations and plain
 * thank-you pages that do not render a form block, the block's
 * `carrot/form/script` never loads — so this module is enqueued on every
 * frontend page to guarantee the deferred event still fires. See #9220.
 */
const replayPendingLeadEvents = () => {
  const submissions = getSubmissions();
  const pending = Object.values(submissions).filter(
    (submission) => submission?.eventposted === 0
  );

  if (pending.length === 0) return;

  pending.forEach((submission) => {
    fireLeadEvent({
      formSubmitId: submission.id,
      formSubmitPage: submission.page,
      formSubmitTitle: submission.title,
      formSubmitNumOfFields: submission.numOfFields,
      formSubmitLocation: submission.formlocation,
      formSubmitSiteId: submission.siteid,
    });
    submission.eventposted = 1;
  });

  setSubmissions(submissions);
};

/**
 * Redirect returning visitors past forms with "Return access" enabled.
 *
 * Forms whose confirmation enables the return-access setting render a hidden
 * `gform_return_access` input (oc-functionality-gforms-return-access.php).
 * Once the visitor has a matching submission in the `oncarrot_forms` cookie,
 * they are sent straight to the confirmation `returnurl` instead of being
 * asked to register again.
 *
 * This runs here — on every frontend page — rather than in the block-only
 * `carrot/form/script`, because gated forms are frequently rendered via the
 * `[gravityform]` shortcode or `GFForms::get_form()` on classic themes, where
 * the block script never loads. See #9486.
 *
 * Every clause is scoped to a content container — `.main` (classic themes),
 * `.entry-content` (spark's wp-block-post-content, classic posts and
 * location/property singles), or the lead-page `.sidebar` where base-lead.php
 * puts the lead capture form. Sitewide injections — the sticky lead-gen
 * banner and G'Day bar (`wp_body_open` / `wp_footer`) and the property
 * inquiry modal (theme headers) — all render outside these containers and
 * MUST stay unmatched: they appear on every page, so a redirect match there
 * would hijack the entire site for returning visitors. Never add an unscoped
 * `body.x .gform_wrapper` clause here. The banner/bar guard below is a
 * belt-and-suspenders check on top of that scoping.
 */
const CONTENT_FORM_SELECTOR = [
  'body.page .main .gform_wrapper',
  'body.page .entry-content .gform_wrapper',
  'body.single-post .entry-content .gform_wrapper',
  'body.single-lead .main .gform_wrapper',
  'body.single-lead .sidebar .gform_wrapper',
  'body.single-location .entry-content .gform_wrapper',
  // The classic theme's property inquiry widget renders after .entry-content
  // closes (content-single-property.php), directly inside .main — so property
  // singles need the .main scope, not .entry-content. Popup mode stays safe:
  // the modal renders in the theme header, outside .main, and is guarded below.
  'body.single-property .main .gform_wrapper',
  'body.single-property .entry-content .gform_wrapper',
].join(', ');

const processReturnAccessRedirects = () => {
  const submissions = getSubmissions();

  document.querySelectorAll(CONTENT_FORM_SELECTOR).forEach((element) => {
    if (element.closest('#carrot-lead-generation-banner, .gday-bar, .modal'))
      return;
    if (!element.querySelector('input[name="gform_return_access"]')) return;

    const form = element.getElementsByTagName('form')[0];
    const elementId = form?.getAttribute('id');
    if (!elementId) return;

    const instanceId = parseInt(elementId.replace('gform_', ''));

    // TODO: better understand these values and parseInt() to restore a "===" check
    if (submissions[instanceId]?.id == instanceId) {
      window.location.href = submissions[instanceId].returnurl;
    }
  });
};

const onFrontendPageLoad = () => {
  replayPendingLeadEvents();
  processReturnAccessRedirects();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', onFrontendPageLoad, {
    once: true,
  });
} else {
  onFrontendPageLoad();
}
